import { createFileRoute, Link } from "@tanstack/react-router";
import { SafeImage } from "@/components/site/SafeImage";
import { useMemo, useState } from "react";
import { articlesForCountry } from "@/lib/article-index.generated";

const PORTUGAL_ARTICLES = articlesForCountry("portugal");
import { Search, Clock, Calendar, ChevronLeft } from "lucide-react";
import { ArticleDisclaimer } from "@/components/site/ArticleDisclaimer";
import { socialImageMeta } from "@/lib/social";
import { makeSeoDescription } from "@/lib/seo-description";

const CANONICAL = "https://lifewestway.com/portugal/articles";
const TITLE = "Portugal Articles 2026 | LifeWestWay";

export const Route = createFileRoute("/portugal/articles/")({
  head: () => ({
    meta: [
      { title: TITLE },
      {
        name: "description",
        content:
          makeSeoDescription("Browse every Portugal guide on LifeWestWay — student visa, cost of living, English-taught programs, tech jobs, and citizenship pathway. Search and filter by category."),
      },
      { property: "og:title", content: TITLE },
      {
        property: "og:description",
        content:
          makeSeoDescription("Search and filter every LifeWestWay Portugal guide by category — immigration, cost of living, education, careers, and citizenship."),
      },
      { property: "og:type", content: "website" },
      { property: "og:url", content: CANONICAL },
      ...socialImageMeta(),
      { name: "twitter:title", content: TITLE },
    ],
    links: [{ rel: "canonical", href: CANONICAL }],
  }),
  component: PortugalArticlesIndex,
});

function PortugalArticlesIndex() {
  const [query, setQuery] = useState("");
  const [category, setCategory] = useState<string>("All");

  const categories = useMemo(() => {
    const set = new Set<string>();
    PORTUGAL_ARTICLES.forEach((a) => set.add(a.category));
    return ["All", ...Array.from(set).sort()];
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return PORTUGAL_ARTICLES.filter((a) => {
      if (category !== "All" && a.category !== category) return false;
      if (!q) return true;
      return (
        a.title.toLowerCase().includes(q) ||
        a.description.toLowerCase().includes(q) ||
        a.category.toLowerCase().includes(q)
      );
    });
  }, [query, category]);

  return (
    <div className="min-h-screen bg-background">
      <div className="mx-auto max-w-6xl px-4 py-10 sm:py-14">
        <Link title="Back to the Portugal country guide"
          to="/$country"
          params={{ country: "portugal" }}
          className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-primary"
        >
          <ChevronLeft className="h-4 w-4" /> Back to Portugal
        </Link>

        <header className="mt-4 mb-8">
          <p className="text-xs font-semibold uppercase tracking-widest text-primary">
            Portugal · Article Library
          </p>
          <h1 className="mt-2 font-serif text-3xl sm:text-5xl font-bold text-foreground">
            All Portugal Articles
          </h1>
          <p className="mt-3 max-w-2xl text-muted-foreground">
            Every LifeWestWay guide on studying, working, and settling in Portugal — from Type D
            student visas to citizenship after five years.
          </p>
        </header>

        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between border border-border rounded-xl p-4 bg-card">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <input
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search Portugal articles…"
              aria-label="Search Portugal articles"
              className="w-full rounded-lg border border-border bg-background pl-9 pr-3 py-2 text-sm outline-none focus:border-primary focus:ring-2 focus:ring-primary/20"
            />
          </div>
          <div className="flex flex-wrap gap-2">
            {categories.map((c) => (
              <button
                key={c}
                type="button"
                onClick={() => setCategory(c)}
                className={`rounded-full px-3 py-1.5 text-xs font-medium border transition ${
                  category === c
                    ? "bg-primary text-primary-foreground border-primary"
                    : "bg-background text-foreground border-border hover:border-primary/60"
                }`}
                aria-pressed={category === c}
              >
                {c}
              </button>
            ))}
          </div>
        </div>

        <p className="mt-4 text-sm text-muted-foreground">
          Showing <span className="font-semibold text-foreground">{filtered.length}</span> of{" "}
          {PORTUGAL_ARTICLES.length} articles
        </p>

        {filtered.length === 0 ? (
          <div className="mt-10 rounded-xl border border-dashed border-border p-10 text-center">
            <p className="text-muted-foreground">
              No articles match your search. Try a different keyword or category.
            </p>
          </div>
        ) : (
          <ul className="mt-6 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {filtered.map((a) => (
              <li key={a.slug}>
                <Link title={`Read: ${a.title} (Portugal guide)`}
                  to="/portugal/$slug"
                  params={{ slug: a.slug }}
                  className="group block h-full overflow-hidden rounded-xl border border-border bg-card transition hover:border-primary/50 hover:shadow-lg"
                >
                  <div className="aspect-[16/9] overflow-hidden bg-muted">
                    <SafeImage width={1200} height={675}
                      src={a.heroImage}
                      alt={a.title}
                      loading="lazy" decoding="async"
                      className="h-full w-full object-cover transition group-hover:scale-105"
                    />
                  </div>
                  <div className="p-4">
                    <span className="inline-block rounded-full bg-primary/10 px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wider text-primary">
                      {a.category}
                    </span>
                    <h2 className="mt-2 font-serif text-lg font-semibold leading-snug text-foreground group-hover:text-primary">
                      {a.title}
                    </h2>
                    <p className="mt-2 line-clamp-3 text-sm text-muted-foreground">
                      {a.description}
                    </p>
                    <div className="mt-3 flex items-center gap-4 text-xs text-muted-foreground">
                      <span className="inline-flex items-center gap-1">
                        <Clock className="h-3 w-3" /> {a.readMinutes} min read
                      </span>
                      <span className="inline-flex items-center gap-1">
                        <Calendar className="h-3 w-3" /> {a.updatedAt}
                      </span>
                    </div>
                  </div>
                </Link>
              </li>
            ))}
          </ul>
        )}

        <section className="mt-16 rounded-3xl border border-border/60 bg-card/40 p-6 sm:p-10">
          <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-accent">
            Portugal · Editor&apos;s overview
          </p>

          <h2 className="mt-4 font-display text-2xl font-bold tracking-tight text-accent sm:text-3xl">
            How to use this Portugal library
          </h2>
          <p className="mt-4 text-[15px] leading-7 text-foreground/80">
            Portugal attracts three very different groups, and the guide you need depends on which
            one you belong to. Students come for English-taught degrees at a fraction of Northern
            European tuition, with living costs that still allow a modest budget in Lisbon, Porto,
            Coimbra, or Braga. Remote workers and retirees come for the D8 and D7 routes, which trade
            a job offer for proof of stable income. Skilled workers and family members come through
            employment or reunification, usually with an eye on the five-year clock towards permanent
            residency and citizenship. The articles above are written for each of these paths
            separately rather than blended into one generic overview.
          </p>
          <p className="mt-4 text-[15px] leading-7 text-foreground/80">
            Whichever route applies, the sequence is broadly the same: obtain a NIF tax number, open
            a Portuguese bank account, secure a registered address or lease, arrange health cover,
            then attend the consular appointment and later the AIMA residency step in Portugal.
            Appointment availability — not eligibility — is the usual bottleneck, so start earlier
            than the published processing times suggest.
          </p>

          <h3 className="mt-8 font-display text-lg font-semibold text-accent/90 sm:text-xl">
            Start with money, not paperwork
          </h3>
          <p className="mt-2 text-[15px] leading-7 text-foreground/80">
            Almost every Portuguese permit is assessed against income and savings thresholds tied to
            the national minimum wage, with additional percentages for a spouse and each child. Our
            cost-of-living and student-finance guides break those numbers down by city, because a
            budget that works comfortably in Braga or Évora will be tight in central Lisbon, where
            rents have risen fastest.
          </p>

          <h3 className="mt-8 font-display text-lg font-semibold text-accent/90 sm:text-xl">
            Then plan the five-year horizon
          </h3>
          <p className="mt-2 text-[15px] leading-7 text-foreground/80">
            Portugal remains one of the shorter routes to citizenship in Western Europe, but the
            clock only counts legal residence, and gaps between permits can reset it. Our citizenship
            and residency articles explain the renewal cycle, the A2 language requirement, and the
            tax-residency consequences of crossing 183 days in a calendar year — a threshold many
            newcomers cross without realising it.
          </p>

          <h4 className="mt-6 text-sm font-semibold uppercase tracking-wide text-foreground">
            Keeping this library current
          </h4>
          <p className="mt-1 text-[15px] leading-7 text-foreground/70">
            Every Portugal guide carries an update date. Fees, wage thresholds, and tax rules change
            with the annual state budget, so we re-verify each article against official sources on a
            rolling schedule and note substantive corrections in the page itself.
          </p>
        </section>

        <ArticleDisclaimer />
      </div>

    </div>
  );
}
